import { Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { SettingsCard } from '../../components/SettingsCard';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useAppSettings,
  useSaveAppSettings,
  useResetAppSetting,
} from './useAppSettings';

/**
 * App-Settings — DB-overridable env-Konfiguration.
 *
 * Quelle: 01_apis/01_core.api.valitype.net/ADMIN/views.md → #/app-settings
 * Route: /admin/settings/app
 *
 * Jede Setting hat einen env-Default und optional einen DB-Override. Operator
 * kann pro Setting den Override setzen (PUT) oder entfernen (DELETE → fällt
 * auf env-Default zurück).
 *
 * Erste produktiv-editierbare Settings-View — etabliert das Pattern für die
 * restlichen Settings-Routen (Core/Nexus/Probe/Pulse).
 */
export function AppSettingsPage() {
  const query = useAppSettings();
  const save = useSaveAppSettings();
  const reset = useResetAppSetting();
  const canWrite = useHasPermission({ perms: ['appSettings.write'] });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="App-Settings" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="App-Settings" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const groups = query.data?.groups ?? [];

  return (
    <div>
      <PageHeader
        title="App-Settings"
        subtitle="DB-overridable env-Konfiguration · GET /v1/admin/settings"
      />

      <Alert color="info" className="small">
        Jede Setting kann per <strong>DB-Override</strong> gegen den env-Default
        umkonfiguriert werden. Reset-Button entfernt den Override —
        Server fällt sofort auf den env-Wert zurück.
        Änderungen werden im <strong>Audit-Log</strong> pro Gruppe geloggt
        (action: <code>app_setting.changed</code>).
      </Alert>

      {groups.map((g) => (
        <SettingsCard
          key={g.title}
          title={g.title}
          description={g.description}
          settings={g.settings}
          canWrite={canWrite}
          busy={save.isPending || reset.isPending}
          onSave={(patch) => save.mutateAsync(patch)}
          onResetField={(key) => reset.mutateAsync({ key })}
        />
      ))}
    </div>
  );
}
